import { Controller } from 'react-hook-form';
import { useState } from 'react';
import copy from 'copy-to-clipboard';
import SidepanelDefaultRow from '../side-panel/side-panel-default-row';
import InputNumber from '../inputs/input-number';
import { RowProps } from './types';
import IconCopy from '../icons/copy-two';
import IconInfo from '../icons/hint';

import styles from './transaction.module.css';

let timeout: NodeJS.Timeout;

export default function SidepanelNumberRow({
  title,
  name,
  value,
  control,
  enableEdit,
  placeholder,
  borderless = false,
  disabled = false,
  error,
  setValue,
  isFixedExchangeAmount,
  suffix,
  tabIndex,
  amount,
  setAmount,
  copyEnabled,
  copyValue,
  exchangeInverseRate,
  exchangeInverseBaseRate,
  exchangeType,
  accountType,
  baseRate,
  exchangeRate,
  hintText,
  withHint,
  rightHint,
  toleranceRow,
  setError,
  clearErrors,
}: RowProps): React.JSX.Element {
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);

  const onCopy = () => {
    setCopyIsSuccess(true);
    copy(copyValue as string);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  let profit: number | undefined;
  let profitString: string | undefined;

  if (amount) {
    if (accountType === 'DEBIT') {
      if (
        exchangeType === 'BUY' &&
        exchangeInverseBaseRate &&
        exchangeInverseRate
      ) {
        profit =
          Number(amount) * (exchangeInverseBaseRate - exchangeInverseRate);
        profitString = `Amount (${amount}) * (Inverse Base Rate (${exchangeInverseBaseRate}) - Inverse Actual Rate (${exchangeInverseRate}))`;
      } else if (exchangeType === 'SELL' && exchangeRate && baseRate) {
        profit = Number(amount) * (exchangeRate - baseRate);
        profitString = `Amount (${amount}) * (Actual Rate (${exchangeRate}) - Base Rate (${baseRate}))`;
      }
    } else if (accountType === 'CREDIT') {
      if (
        exchangeType === 'BUY' &&
        exchangeInverseBaseRate &&
        exchangeInverseRate
      ) {
        profit =
          Number(amount) * (exchangeInverseRate - exchangeInverseBaseRate);
        profitString = `Amount (${amount}) * (Inverse Actual Rate (${exchangeInverseRate}) - Inverse Base Rate (${exchangeInverseBaseRate}))`;
      } else if (exchangeType === 'SELL' && exchangeRate && baseRate) {
        profit = Number(amount) * (baseRate - exchangeRate);
        profitString = `Amount (${amount}) * (Base Rate (${baseRate}) -  Actual Rate (${exchangeRate}))`;
      }
    }
  }

  let content;

  if (enableEdit) {
    content = (
      <Controller
        name={name}
        control={control}
        rules={{ required: false }}
        defaultValue={value}
        render={({ field }) => (
          <>
            {name === 'exchangeProfit' && (
              <div className={styles.exchangeTitleWrapper}>
                <div className={styles.exchangeTitle}>{title}</div>
                {amount && baseRate && (
                  <div className={styles.profitHint}>
                    <IconInfo />
                    <div
                      className={styles.hint}
                    >{`Profit: ${profitString} = ${profit?.toFixed(2)}`}</div>
                  </div>
                )}
              </div>
            )}
            {withHint && hintText && (
              <div className={styles.exchangeTitleWrapper}>
                <div className={styles.exchangeTitle}>{title}</div>
                <div className={styles.profitHint}>
                  <IconInfo />
                  <div
                    className={`${styles.hint} ${
                      rightHint ? styles.rightHint : ''
                    }`}
                  >
                    {hintText}
                  </div>
                </div>
              </div>
            )}
            <InputNumber
              title={
                name === 'exchangeProfit' || (withHint && hintText) ? '' : title
              }
              name={name}
              value={setAmount ? amount : field.value}
              onChange={(e) => {
                if (toleranceRow) {
                  let tolerance = e.target.value;

                  if (clearErrors) {
                    clearErrors(['exchangeRateTolerance']);
                  }

                  if (Number(tolerance) > 20) {
                    tolerance = '20';
                  } else if (tolerance && Number(tolerance) < 0) {
                    tolerance = '0.01';
                  } else if (setError && tolerance && Number(tolerance) === 0) {
                    setError('exchangeRateTolerance', {
                      type: 'custom',
                      message: 'Exchange Rate Tolerance must be 0.01 or higher',
                    });
                  }

                  field.onChange(tolerance);
                } else {
                  // eslint-disable-next-line no-lonely-if
                  if (setAmount) {
                    clearTimeout(timeout);

                    if (e.target.value) {
                      setAmount(e.target.value.replace(/[^\d.]/g, ''));
                    } else {
                      setAmount(undefined);
                    }

                    timeout = setTimeout(() => {
                      field.onChange(e.target.value.replace(/[^\d.]/g, ''));
                    }, 300);
                  } else {
                    field.onChange(e.target.value.replace(/[^\d.]/g, ''));
                  }
                }

                if (setValue && isFixedExchangeAmount) {
                  setValue('sum', e.target.value.replace(/[^\d.]/g, ''));
                }
              }}
              suffix={suffix}
              placeholder={placeholder as string}
              className={borderless ? 'borderless' : ''}
              disabled={disabled}
              tabIndex={tabIndex}
              error={error}
              labelClassName={styles.inputWithSuffix}
            />
          </>
        )}
      />
    );
  } else if (!enableEdit && name === 'exchangeProfit') {
    content = (
      <div className={styles.profitContentWrapper}>
        {value}
        <div className={styles.exchangeTitleWrapper}>
          {amount && baseRate && profitString && profit && (
            <div className={styles.profitHint}>
              <IconInfo />
              <div
                className={styles.hint}
              >{`Profit: ${profitString} = ${profit?.toFixed(2)}`}</div>
            </div>
          )}
        </div>
      </div>
    );
  } else {
    content = value;
  }

  return (
    <SidepanelDefaultRow
      title={enableEdit ? undefined : title}
      valueClassName={`${copyEnabled && enableEdit ? styles.rowWithCopy : ''} ${
        copyEnabled && !enableEdit ? styles.readCopyButtonWrapper : ''
      }`}
    >
      {content}
      {copyEnabled && (
        <>
          {enableEdit && (
            <button
              type="button"
              className={`${styles.copyButton} ${
                styles.autocompleteCopyButton
              } ${copyIsSuccess ? styles.success : ''}`}
              onClick={onCopy}
              aria-label="Copy"
            >
              <IconCopy />
            </button>
          )}
          {!enableEdit && (
            <button
              type="button"
              className={`${styles.readCopyButton} ${
                copyIsSuccess ? styles.success : ''
              }`}
              onClick={onCopy}
              aria-label="Copy"
            >
              <IconCopy />
            </button>
          )}
        </>
      )}
    </SidepanelDefaultRow>
  );
}
